import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { usePermissions } from 'PermissionContext';
import type FundOpportunity from '../../models/FundOpportunity';
import FundEventsSection from '../admin/FundOpportunityAdmin/FundEventsSection';
import MessageToast from '../MessageToast';
import type {
  PageMode,
  SaveStatus,
} from '../admin/newContentManagement/newContentManagementEditorTypes';
import FundUpdatesAdminDialogs from './FundUpdatesAdminDialogs';
import type { FundUpdatesAdminPanelProps } from './fundUpdatesAdmin.types';
import {
  ExistingUpdatesSection,
  NewUpdatesSection,
} from './SimplifiedFundUpdatesAdminPanelSections';
import { useFundUpdatesAdmin } from './useFundUpdatesAdmin';
import { useFundUpdatesPanelToolbar } from './useFundUpdatesPanelToolbar';

export interface FundManagementToolbarState {
  saveStatus?: SaveStatus;
  pageMode?: PageMode;
  setPageMode?: (mode: PageMode) => void;
  saveNow?: () => void;
  toolbarRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * Page-wide actions, independent of whichever single update is "open" —
   * the page toolbar only ever offers Publish All / Revert To Published.
   * Publishing or reverting a single update is handled by that update's own
   * row controls, not the page toolbar.
   */
  onPublishAll?: () => void;
  publishAllDisabled?: boolean;
  publishingAll?: boolean;
  onRevertAll?: () => void;
  revertAllDisabled?: boolean;
  revertingAll?: boolean;
}

interface SimplifiedFundUpdatesAdminPanelProps extends FundUpdatesAdminPanelProps {
  fundOpportunity: FundOpportunity;
  /** Reports the open update's toolbar state up to the page-level header. */
  onToolbarStateChange?: (
    state:
      | FundManagementToolbarState
      | null
      | ((prev: FundManagementToolbarState | null) => FundManagementToolbarState | null),
  ) => void;
}

function LoadingPanel() {
  return (
    <Box sx={{ pt: 5, display: 'flex', justifyContent: 'center', minHeight: '30vh' }}>
      <CircularProgress />
    </Box>
  );
}

function FundEventsToast({
  message,
  type,
  onClose,
}: {
  message: string | null;
  type: 'error' | 'warning' | 'info' | 'success' | null;
  onClose: () => void;
}) {
  return (
    <MessageToast
      message={message ?? ''}
      type={type ?? 'info'}
      open={!!message}
      onClose={onClose}
      autoHideDuration={5000}
    />
  );
}

function PanelDialogs({ controller }: { controller: ReturnType<typeof useFundUpdatesAdmin> }) {
  const { state, setState, handleConfirmDelete, handleConfirmSaveAndGenerate } = controller;
  return (
    <FundUpdatesAdminDialogs
      deleteConfirm={state.deleteConfirm}
      deleteInProgress={state.deleteInProgress}
      generateConfirm={state.generateConfirm}
      onCloseDeleteConfirm={() => setState((prev) => ({ ...prev, deleteConfirm: null }))}
      onConfirmDelete={handleConfirmDelete}
      onCloseGenerateConfirm={() => setState((prev) => ({ ...prev, generateConfirm: null }))}
      onConfirmSaveAndGenerate={handleConfirmSaveAndGenerate}
    />
  );
}

export default function SimplifiedFundUpdatesAdminPanel({
  fundOpportunity,
  fundOpportunityId,
  fundName,
  onToolbarStateChange,
}: SimplifiedFundUpdatesAdminPanelProps) {
  const controller = useFundUpdatesAdmin({ fundOpportunityId });
  const { state } = controller;
  const sharedProps = { controller, fundOpportunity, fundName };
  const { hasPermission } = usePermissions();

  const [fundEventsToastMessage, setFundEventsToastMessage] = useState<string | null>(null);
  const [fundEventsToastType, setFundEventsToastType] = useState<
    'error' | 'warning' | 'info' | 'success' | null
  >(null);

  const { openUpdateId, setOpenUpdateId, handleToolbarStateChange } = useFundUpdatesPanelToolbar(
    controller,
    onToolbarStateChange,
  );

  if (state.loading) return <LoadingPanel />;

  return (
    <Box sx={{ overflowAnchor: 'none' }}>
      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.error}
        </Alert>
      )}
      <NewUpdatesSection
        {...sharedProps}
        openUpdateId={openUpdateId}
        onOpen={setOpenUpdateId}
        onToolbarStateChange={handleToolbarStateChange}
      />
      <ExistingUpdatesSection
        {...sharedProps}
        openUpdateId={openUpdateId}
        onOpen={setOpenUpdateId}
        onToolbarStateChange={handleToolbarStateChange}
      />
      <Box sx={{ mb: 3 }}>
        <FundEventsSection
          fundOpportunityId={fundOpportunityId}
          hasPermission={hasPermission}
          onShowToast={(message, type) => {
            setFundEventsToastMessage(message);
            setFundEventsToastType(type);
          }}
        />
      </Box>
      <PanelDialogs controller={controller} />
      <FundEventsToast
        message={fundEventsToastMessage}
        type={fundEventsToastType}
        onClose={() => {
          setFundEventsToastMessage(null);
          setFundEventsToastType(null);
        }}
      />
    </Box>
  );
}
